/**
 * Where "Capture Issues" opens a team review WITHOUT asking for a URL, and
 * when it must still ask.
 *
 * Emily, 2026-09-10, on a bonkers.ie project created through MCP whose Capture
 * Issues modal opened on an EMPTY Page URL field: "it should have had the url
 * added already ... it should just open the teamreview on the default project
 * url". The Issues page resolved its pre-fill from the newest issue or the
 * project's scan URL only, and the project's primary domain (the Edit Project
 * "URL" field, which MCP's create_project also sets) was never a rung.
 * Measured read-only on production 2026-09-10 across 325 live external
 * projects: 238 carry a primary domain, 2 carry a scan URL, 97 carry any
 * issue, so 226 opened the modal blank and only 85 still would with this rung.
 *
 * Precedence, in order:
 *   0. the project's REVIEW ENTRY URL, the one rung the user STATES rather
 *      than the product inferring it (Emily, 2026-09-10, D1 option A). It
 *      outranks the newest issue deliberately: an inference that moves under
 *      the user is the thing being fixed. SINCE 2026-09-17 no dashboard screen
 *      SETS it: the "Review page URL" field left with the Edit modal when it
 *      folded into the Project Settings modal (Emily: "remove review page url
 *      option from modal, the review page should be the default unless
 *      changed"), so today it only ever holds a value written through the API
 *      or MCP. The rung stays because a value written there must still be
 *      honoured; measured read-only on production 2026-09-17, 0 of 685 live
 *      projects carry one, so every project resolves from rung 1 down. It is
 *      its own column because
 *      `primary_domain` is read as a bare HOST by thumbnailCapture,
 *      thumbnailRetry.service, scheduledScanReanchor, the project card
 *      subtitle, project search and MCP's `https://${primaryDomain}`, so it
 *      cannot carry a path;
 *   1. the NEWEST issue's page URL, by createdAt (the list the page holds is
 *      sorted however the user sorted it, so position 0 is not "newest");
 *   2. the project's scan URL;
 *   3. `https://` + the project's primary domain (shared with the Create
 *      Client Review modal's own pre-fill, clientReviewInitialUrl.ts);
 *   4. nothing: the caller must ask (the modal).
 *
 * `reachable` is the second half of the answer. A resolved URL the server
 * proxy cannot reach (localhost, a private address, a file:// path) still
 * needs the modal, because the modal is where the extension hint lives. So
 * does OverlayQA's own app (`own-app`): a review of our dashboard can never
 * render (incident 2026-09-10, bonkers.ie, after the extension had stamped
 * app.overlayqa.com onto the project's domain), and the modal is where the
 * explanation lives.
 * `classifyUnreachableUrl` is the ONE source for that rule; LiveReviewModal
 * reads it too, so the two cannot drift.
 *
 * Pure: takes whatever the queries hold (undefined while loading is fine),
 * never throws.
 */
import { classifyOwnAppUrl } from '@overlayqa/shared';
import { projectDefaultUrl } from './clientReviewInitialUrl';

export type CaptureIssuesUrlSource = 'entry_url' | 'last_issue' | 'scan_url' | 'primary_domain' | 'none';
export type UnreachableReason = 'localhost' | 'private' | 'local-file' | 'own-app';

export interface CaptureIssuesUrl {
  /** The URL to open the review at, or null when nothing is known. */
  url: string | null;
  source: CaptureIssuesUrlSource;
  /** False when `url` is null OR the proxy cannot reach it (see unreachableReason). */
  reachable: boolean;
  unreachableReason: UnreachableReason | null;
}

interface IssueLike {
  pageUrl?: string | null;
  createdAt?: string | Date | null;
}

interface ProjectLike {
  /** Full URL incl. path and query, exactly as the user typed it. */
  reviewEntryUrl?: string | null;
  scanUrl?: string | null;
  primaryDomain?: string | null;
}

/**
 * Pre-flight for URLs the server-side proxy cannot reach. Moved verbatim from
 * LiveReviewModal (2026-09-10) so the direct path and the modal agree.
 */
export function classifyUnreachableUrl(raw: string): UnreachableReason | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Catch file:// and other non-http protocols before URL parsing prepends https://
  if (/^file:/i.test(trimmed)) return 'local-file';
  if (/^(?!https?:\/\/)[a-z][a-z0-9+.-]*:/i.test(trimmed)) return 'localhost';

  try {
    const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    const h = url.hostname.toLowerCase();
    if (h === 'localhost' || h === '0.0.0.0') return 'localhost';
    if (
      /^127\./.test(h) ||
      /^10\./.test(h) ||
      /^192\.168\./.test(h) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(h)
    ) {
      return 'private';
    }
    // Our own dashboard, API root or review page: public, resolvable, and
    // never a page to review. The sandbox demo passes (a page we own on purpose).
    if (classifyOwnAppUrl(url.href)) return 'own-app';
    return null;
  } catch {
    return null;
  }
}

function toTime(value: string | Date | null | undefined): number {
  if (!value) return 0;
  const t = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
}

function newestIssueUrl(issues: ReadonlyArray<IssueLike> | undefined): string | null {
  if (!issues || issues.length === 0) return null;
  let best: { url: string; at: number } | null = null;
  for (const issue of issues) {
    const url = (issue.pageUrl ?? '').trim();
    if (!url) continue;
    const at = toTime(issue.createdAt);
    // Strictly greater: on a tie the earlier list position wins.
    if (!best || at > best.at) best = { url, at };
  }
  return best?.url ?? null;
}

function entryUrl(project: ProjectLike | undefined): string | null {
  const url = (project?.reviewEntryUrl ?? '').trim();
  return url || null;
}

function scanUrl(project: ProjectLike | undefined): string | null {
  const url = (project?.scanUrl ?? '').trim();
  return url || null;
}

export function resolveCaptureIssuesUrl(input: {
  issues?: ReadonlyArray<IssueLike> | undefined;
  project?: ProjectLike | undefined;
}): CaptureIssuesUrl {
  const fromEntry = entryUrl(input.project);
  const fromIssue = fromEntry ? null : newestIssueUrl(input.issues);
  const fromScan = fromEntry || fromIssue ? null : scanUrl(input.project);
  const fromDomain = fromEntry || fromIssue || fromScan ? null : projectDefaultUrl(input.project);

  const url = fromEntry ?? fromIssue ?? fromScan ?? fromDomain;
  if (!url) return { url: null, source: 'none', reachable: false, unreachableReason: null };

  const source: CaptureIssuesUrlSource = fromEntry
    ? 'entry_url'
    : fromIssue
      ? 'last_issue'
      : fromScan
        ? 'scan_url'
        : 'primary_domain';
  const unreachableReason = classifyUnreachableUrl(url);
  return { url, source, reachable: unreachableReason === null, unreachableReason };
}

/**
 * The short label the split button wears, so the page it opens is legible
 * BEFORE the click (Emily, 2026-09-10, D2 option C, wireframe approved:
 * "[ Review www.bonkers.ie/compare-… ↗ | ▾ ]").
 *
 * Scheme dropped (noise), `www.` KEPT (it is part of the host that actually
 * answers, and confusing it with the apex is what broke her morning), path and
 * query kept until the budget, then one ellipsis.
 *
 * MAX_TARGET_LABEL is a CEILING on a shared toolbar that already carries
 * "Create Client Link", so it was MEASURED rather than chosen by feel:
 * verify-review-entry-url.ts renders the real toolbar at 1440 and 1280 and
 * asserts the primary and "Create Client Link" share a row. Measured on the
 * mini 2026-09-10 at a full-width label ("Review api.overlayqa.com/sandbox/?pa…"):
 * both widths y=396.2 for both buttons, so 30 fits with the longest label.
 */
const MAX_TARGET_LABEL = 30;

export function describeCaptureTarget(url: string | null | undefined): string | null {
  const raw = (url ?? '').trim();
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  const path = `${parsed.pathname === '/' ? '' : parsed.pathname}${parsed.search}`;
  const full = `${parsed.hostname}${path}`;
  if (full.length <= MAX_TARGET_LABEL) return full;
  return `${full.slice(0, MAX_TARGET_LABEL - 1)}\u2026`;
}

/**
 * The ONE wording of the capture action, so the two controls that open it on
 * one screen cannot drift.
 *
 * The Issues page renders this action twice on an empty project: the toolbar's
 * split button and IssuesEmptyState's button six rows below it. The empty
 * state's docstring had required them to match verbatim since 2026-08-25 and
 * nothing enforced it, so the day the toolbar learned to name its target the
 * two read differently for the same click ("Review api.overlayqa.com/sandbox/"
 * above "Capture Issues"). Pinned by __tests__/captureLabelParity.test.tsx.
 */
export function captureIssuesLabel(url: string | null | undefined): string {
  const target = describeCaptureTarget(url);
  return target ? `Review ${target}` : 'Capture issues';
}
